import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { testimonials } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9q9svknlsngrm4llu8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage(serverMsg);
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 寸止挑战训练中心 | 品牌介绍·口碑评价·联系我们"
        description="寸止挑战训练中心专注于极限控制力与专注度训练。了解我们的品牌故事、核心团队、用户口碑，以及如何联系我们获取专业的寸止挑战训练指导。"
        keywords="寸止挑战,关于我们,寸止挑战训练中心,极限控制力训练,联系我们"
        canonicalUrl="https://cunzhitiaozhan.com/about"
      />

      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
              关于寸止挑战训练中心
            </h1>
            <div className="relative h-56 md:h-72 rounded-xl overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20training%20center%20interior%20with%20clean%20minimalist%20design%20wooden%20floors%20natural%20light%20streaming%20through%20large%20windows%20calm%20professional%20atmosphere%20editorial%20photography%20amber%20earthy%20tones&width=1200&height=500&seq=cunzhi-about-hero&orientation=landscape"
                alt="寸止挑战训练中心"
                title="寸止挑战训练中心"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-base max-w-none prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-sm md:prose-p:text-base prose-strong:text-foreground-900 prose-strong:font-semibold">
              <p>
                <strong>寸止挑战训练中心</strong>成立于2024年，是国内最早专注于寸止挑战训练方法研究与推广的专业机构之一。我们的使命是通过科学的训练体系和优质的内容输出，帮助每一位挑战者突破自我极限，掌握真正的<strong>寸止精髓</strong>。
              </p>
              <p>
                两年来，我们累计服务了超过50,000名挑战者，从完全零基础的入门新手，到追求极致表现的高阶玩家，我们为不同水平的挑战者提供针对性的训练指导。我们的训练方法论融合了现代心理学、运动生理学和东方智慧的精髓，形成了独具特色的"寸止训练体系"。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-user-star-line text-2xl" />
                </div>
                <div className="text-2xl font-bold font-heading text-foreground-900 mb-1">50,000+</div>
                <div className="text-sm text-foreground-500">服务挑战者</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-article-line text-2xl" />
                </div>
                <div className="text-2xl font-bold font-heading text-foreground-900 mb-1">30+</div>
                <div className="text-sm text-foreground-500">专业文章</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                  <i className="ri-star-line text-2xl" />
                </div>
                <div className="text-2xl font-bold font-heading text-foreground-900 mb-1">4.8</div>
                <div className="text-sm text-foreground-500">用户评分</div>
              </div>
            </div>
          </section>

          <section id="testimonials" className="mb-16">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold mb-3">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                他们怎么说
              </h2>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                来自真实用户的反馈与评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < t.rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-300'}`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="bg-background-100 rounded-2xl p-6 md:p-10">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold mb-3">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                有任何问题？欢迎留言
              </h2>
              <p className="text-foreground-500 text-sm max-w-md mx-auto">
                我们会在24小时内回复你的留言
              </p>
            </div>

            <form onSubmit={handleSubmit} className="max-w-lg mx-auto" data-readdy-form="">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入你的留言内容（不超过500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  className="spam-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                    <i className="ri-checkbox-circle-line" />
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                    <i className="ri-error-warning-line" />
                    {formMessage}
                  </div>
                )}
              </div>
            </form>

            <div className="max-w-lg mx-auto mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line" />
                </div>
                <div>
                  <div className="text-xs text-foreground-400">邮箱</div>
                  <div className="text-sm font-medium text-foreground-900">contact@cunzhitiaozhan.com</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-phone-line" />
                </div>
                <div>
                  <div className="text-xs text-foreground-400">电话</div>
                  <div className="text-sm font-medium text-foreground-900">400-888-9999</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <i className="ri-map-pin-line" />
                </div>
                <div>
                  <div className="text-xs text-foreground-400">地址</div>
                  <div className="text-sm font-medium text-foreground-900">北京市朝阳区</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}